import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import Analytics from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Form validation schema
const contactFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name is too long'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Please enter a valid phone number').max(20, 'Phone number is too long'),
  company: z.string().optional(),
  message: z.string().min(10, 'Message must be at least 10 characters').max(1000, 'Message is too long')
});

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: zodResolver(contactFormSchema)
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Track form submission attempt
      Analytics.trackFormSubmission('contact_form', {
        has_company: !!data.company,
        message_length: data.message.length
      });

      const response = await fetch(`${BACKEND_URL}/api/contact-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const result = await response.json();
      
      // Track successful submission
      Analytics.trackEvent('contact_form_success', {
        submission_id: result.id
      });

      setSubmitStatus('success');
      reset();

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);

    } catch (error) {
      console.error('Form submission error:', error);
      
      // Track failed submission
      Analytics.trackEvent('contact_form_error', {
        error: error.message
      });

      setSubmitStatus('error');

      // Reset error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="border-[#00FFB2]/50 bg-gradient-to-br from-slate-900/90 to-blue-900/80 backdrop-blur-md shadow-2xl shadow-[#00FFB2]/10">
      <CardHeader>
        <CardTitle className="text-2xl text-white">Send Us a Message</CardTitle>
        <CardDescription className="text-gray-300">
          Fill out the form below and we'll get back to you within 24 hours
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-gray-200 font-medium">
              Full Name <span className="text-red-400">*</span>
            </Label>
            <Input
              id="name"
              placeholder="John Doe"
              {...register('name')}
              className={`border-[#00FFB2]/40 bg-slate-800/60 text-white placeholder:text-gray-400 focus:border-[#00FFB2] focus:ring-[#00FFB2] focus:bg-slate-800 ${
                errors.name ? 'border-red-400' : ''
              }`}
            />
            {errors.name && (
              <p className="text-sm text-red-300">{errors.name.message}</p>
            )}
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-200 font-medium">
              Email Address <span className="text-red-400">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              {...register('email')}
              className={`border-[#00FFB2]/40 bg-slate-800/60 text-white placeholder:text-gray-400 focus:border-[#00FFB2] focus:ring-[#00FFB2] focus:bg-slate-800 ${
                errors.email ? 'border-red-400' : ''
              }`}
            />
            {errors.email && (
              <p className="text-sm text-red-300">{errors.email.message}</p>
            )}
          </div>

          {/* Phone Field */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-gray-200 font-medium">
              Phone Number <span className="text-red-400">*</span>
            </Label>
            <Input
              id="phone"
              type="tel"
              placeholder="+91 98765 43210"
              {...register('phone')}
              className={`border-[#00FFB2]/40 bg-slate-800/60 text-white placeholder:text-gray-400 focus:border-[#00FFB2] focus:ring-[#00FFB2] focus:bg-slate-800 ${
                errors.phone ? 'border-red-400' : ''
              }`}
            />
            {errors.phone && (
              <p className="text-sm text-red-300">{errors.phone.message}</p>
            )}
          </div>

          {/* Company Field (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="company" className="text-gray-200 font-medium">
              Company Name (Optional)
            </Label>
            <Input
              id="company"
              placeholder="Your Company"
              {...register('company')}
              className="border-[#00FFB2]/40 bg-slate-800/60 text-white placeholder:text-gray-400 focus:border-[#00FFB2] focus:ring-[#00FFB2] focus:bg-slate-800"
            />
          </div>

          {/* Message Field */}
          <div className="space-y-2">
            <Label htmlFor="message" className="text-gray-200 font-medium">
              Message <span className="text-red-400">*</span>
            </Label>
            <Textarea
              id="message"
              placeholder="Tell us about your AI needs and how we can help..."
              rows={5}
              {...register('message')}
              className={`border-[#00FFB2]/40 bg-slate-800/60 text-white placeholder:text-gray-400 focus:border-[#00FFB2] focus:ring-[#00FFB2] focus:bg-slate-800 resize-none ${
                errors.message ? 'border-red-400' : ''
              }`}
            />
            {errors.message && (
              <p className="text-sm text-red-300">{errors.message.message}</p>
            )}
          </div>

          {/* Success Message */}
          {submitStatus === 'success' && (
            <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
              <CheckCircle size={20} className="flex-shrink-0" />
              <p className="text-sm">
                Thank you! Your message has been sent successfully. We'll get back to you within 24 hours.
              </p>
            </div>
          )}

          {/* Error Message */}
          {submitStatus === 'error' && (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              <AlertCircle size={20} className="flex-shrink-0" />
              <p className="text-sm">
                Sorry, something went wrong. Please try again or contact us directly via email or phone.
              </p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#00FFB2] hover:bg-[#00D9AA] text-black font-semibold shadow-lg shadow-[#00FFB2]/30"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Message'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
